import { useEffect, useState } from 'react'
import styled from 'styled-components'
import { Card, CardHeader } from '../../styled/cards'
import ButtonSpinnerWhite from '../../layout/spinner/ButtonSpinnerWhite'
import { useTranslation, useTypedSelector, useActions } from '../../../hooks'

interface IsActiveProp {
    isActive: boolean
}

const ModalContainer = styled.div`
    height: 100vh;
    width: 100%;
    background-color: rgba(0, 0, 0, 0.3);
    position: fixed;
    top: 0;
    left: 0;
    z-index: 500;
`

const Modal = styled.div`
    width: 55.6rem;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    z-index: 501;
`

const CancelButton = styled.p`
    font-size: 1.4rem;
    color: ${({ theme }) => `${theme.primary}50`};
    cursor: pointer;
`

const Content = styled.div`
    padding: 2rem 1.4rem;
`

const WorkerList = styled.div`
    max-height: 30rem;
    overflow-y: auto;
    margin-bottom: 1rem;
`

const Checkbox = styled.div<{ isChecked: boolean }>`
    width: 2rem;
    height: 2rem;
    min-width: 2rem;
    border: 2px solid
        ${({ theme, isChecked }) => (isChecked ? theme.blue : theme.grey)};
    border-radius: 0.4rem;
    margin-right: 1.2rem;
    display: flex;
    align-items: center;
    justify-content: center;
    background: ${({ theme, isChecked }) =>
        isChecked ? theme.blue : 'transparent'};
    transition: all 0.15s;
    color: ${({ theme }) => theme.white};
    font-size: 1.2rem;
    font-weight: 700;
`

const WorkerRow = styled.div<{ isSelected: boolean }>`
    display: flex;
    align-items: center;
    padding: 1.2rem 1.4rem;
    border: 2px solid
        ${({ theme, isSelected }) => (isSelected ? theme.blue : theme.grey)};
    border-radius: 0.5rem;
    margin-bottom: 0.8rem;
    cursor: pointer;
    transition: all 0.15s;
    background: ${({ theme, isSelected }) =>
        isSelected ? `${theme.blue}08` : theme.white};

    &:hover {
        border-color: ${({ theme }) => theme.blue};
    }
`

const WorkerInfo = styled.div`
    display: flex;
    flex-direction: column;
`

const WorkerName = styled.p`
    font-size: 1.4rem;
    font-weight: 600;
    color: ${({ theme }) => theme.primary};
`

const WorkerEmail = styled.p`
    font-size: 1.2rem;
    color: ${({ theme }) => theme.greyText};
`

const Button = styled.div<IsActiveProp>`
    height: 5rem;
    width: 100%;
    background-color: ${({ theme, isActive }) =>
        isActive ? `${theme.primary}` : `${theme.primary}50`};
    color: ${({ theme }) => theme.white};
    font-size: 1.6rem;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 0.5rem;
    cursor: ${({ isActive }) => (isActive ? 'pointer' : 'default')};
`

const EmptyText = styled.p`
    font-size: 1.4rem;
    color: ${({ theme }) => theme.greyText};
    text-align: center;
    padding: 2rem 0;
`

interface AssignCaseworkerModalProps {
    closeModal: () => void
    organizationId: number
}

const AssignCaseworkerModal: React.FC<AssignCaseworkerModalProps> = ({
    closeModal,
    organizationId,
}) => {
    const translation = useTranslation()
    const { getAllCaseworkers, assignCaseworkerToOrganization } = useActions()
    const { caseworkers, fetchCaseworkersLoading } = useTypedSelector(
        (state) => state.caseworker.team
    )
    const { lineOfContacts } = useTypedSelector((state) => state.lineOfContact)
    const { organization: selfOrganization } = useTypedSelector(
        (state) => state.caseworker.identity
    )

    const [selectedUserIds, setSelectedUserIds] = useState<number[]>([])
    const [isAssigning, setIsAssigning] = useState(false)

    useEffect(() => {
        if (selfOrganization) {
            getAllCaseworkers(selfOrganization.id)
        }
        // eslint-disable-next-line
    }, [selfOrganization])

    const existingContactIds = lineOfContacts.map((c: any) => c.id)

    const availableWorkers = caseworkers.filter(
        (worker: any) => !existingContactIds.includes(worker.id)
    )

    const toggleUserSelection = (userId: number) => {
        setSelectedUserIds((prev) =>
            prev.includes(userId)
                ? prev.filter((id) => id !== userId)
                : [...prev, userId]
        )
    }

    const handleAssign = async () => {
        if (selectedUserIds.length === 0 || !selfOrganization) return
        setIsAssigning(true)
        let allSucceeded = true
        for (const userId of selectedUserIds) {
            const result: any = await assignCaseworkerToOrganization(
                selfOrganization.id,
                organizationId,
                userId
            )
            if (!result) {
                allSucceeded = false
            }
        }
        setIsAssigning(false)
        if (allSucceeded) {
            closeModal()
        }
    }

    return (
        <ModalContainer>
            <Modal>
                <Card>
                    <CardHeader>
                        {translation.singleClientViewPartner.assignCaseworker ||
                            'Assign caseworker'}
                        <CancelButton onClick={() => closeModal()}>
                            {translation.reusable.cancel}
                        </CancelButton>
                    </CardHeader>
                    <Content>
                        {fetchCaseworkersLoading ? (
                            <EmptyText>Loading...</EmptyText>
                        ) : availableWorkers.length === 0 ? (
                            <EmptyText>
                                {translation.singleClientViewPartner
                                    .noAvailableCaseworkers ||
                                    'No available caseworkers to assign'}
                            </EmptyText>
                        ) : (
                            <WorkerList>
                                {availableWorkers.map((worker: any) => (
                                    <WorkerRow
                                        key={worker.id}
                                        isSelected={selectedUserIds.includes(
                                            worker.id
                                        )}
                                        onClick={() =>
                                            toggleUserSelection(worker.id)
                                        }
                                    >
                                        <Checkbox
                                            isChecked={selectedUserIds.includes(
                                                worker.id
                                            )}
                                        >
                                            {selectedUserIds.includes(
                                                worker.id
                                            ) && '✓'}
                                        </Checkbox>
                                        <WorkerInfo>
                                            <WorkerName>
                                                {worker.first_name ||
                                                worker.firstName
                                                    ? `${
                                                          worker.first_name ||
                                                          worker.firstName ||
                                                          ''
                                                      } ${
                                                          worker.last_name ||
                                                          worker.lastName ||
                                                          ''
                                                      }`.trim()
                                                    : worker.email}
                                            </WorkerName>
                                            {(worker.first_name ||
                                                worker.firstName) && (
                                                <WorkerEmail>
                                                    {worker.email}
                                                </WorkerEmail>
                                            )}
                                        </WorkerInfo>
                                    </WorkerRow>
                                ))}
                            </WorkerList>
                        )}
                        <Button
                            isActive={selectedUserIds.length > 0}
                            onClick={handleAssign}
                        >
                            {isAssigning ? (
                                <ButtonSpinnerWhite />
                            ) : selectedUserIds.length > 1 ? (
                                `${
                                    translation.singleClientViewPartner
                                        .assignCaseworker || 'Assign caseworker'
                                } (${selectedUserIds.length})`
                            ) : (
                                translation.singleClientViewPartner
                                    .assignCaseworker || 'Assign caseworker'
                            )}
                        </Button>
                    </Content>
                </Card>
            </Modal>
        </ModalContainer>
    )
}

export default AssignCaseworkerModal
